import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PiggyBank, Plus, Edit2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import Modal from "./Modal";
import { fmtShekel } from "@/lib/utils";

const TYPES = [
  { k: "pension", label: "פנסיה", color: "#DBEAFE", text: "#1D4ED8" },
  { k: "education_fund", label: "קרן השתלמות", color: "#DCFCE7", text: "#166534" },
  { k: "portfolio", label: "תיק חסכון", color: "#FEE9CC", text: "#9A3412" },
];

export default function SavingsCard() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/savings");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const total = items.reduce((s, x) => s + Number(x.amount || 0), 0);

  const openNew = () =>
    setEditing({ id: null, type: "pension", company: "", amount: "", notes: "" });

  const save = async () => {
    if (!editing.company || !editing.amount) return;
    const payload = {
      type: editing.type,
      company: editing.company,
      amount: parseFloat(editing.amount),
      notes: editing.notes || "",
    };
    if (editing.id) await api.put(`/savings/${editing.id}`, payload);
    else await api.post("/savings", payload);
    setEditing(null);
    load();
  };
  const del = async (id) => {
    await api.delete(`/savings/${id}`);
    load();
  };

  const grouped = TYPES.map((t) => ({
    ...t,
    items: items.filter((i) => i.type === t.k),
    total: items.filter((i) => i.type === t.k).reduce((s, x) => s + Number(x.amount || 0), 0),
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.1 }}
      className="card-surface p-6 md:p-7"
      data-testid="savings-card"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center"
            style={{ background: "#E3F2FD", color: "var(--savings)" }}
          >
            <PiggyBank size={20} />
          </div>
          <div>
            <div className="font-heebo font-bold text-lg text-slate-900">חסכונות</div>
            <div className="text-xs text-slate-500">כל הצבירות שלך</div>
          </div>
        </div>
        <button
          className="btn-secondary text-sm"
          onClick={openNew}
          data-testid="add-saving-btn"
        >
          <Plus size={14} /> הוסף
        </button>
      </div>

      <div className="mb-5">
        <div className="text-xs uppercase text-slate-500 font-heebo">סה״כ צבירה</div>
        <div className="num-pop text-3xl mt-1" style={{ color: "var(--savings)" }} data-testid="savings-total">
          {fmtShekel(total)}
        </div>
      </div>

      <div className="space-y-4">
        {grouped.map((g) => (
          <div key={g.k}>
            <div className="flex items-center justify-between mb-2">
              <span
                className="chip"
                style={{ background: g.color, color: g.text }}
                data-testid={`savings-type-${g.k}`}
              >
                {g.label}
              </span>
              <span className="num-pop text-sm text-slate-700">{fmtShekel(g.total)}</span>
            </div>
            {g.items.length === 0 ? (
              <div className="text-xs text-slate-400 ps-1">אין רשומות</div>
            ) : (
              <div className="space-y-2">
                {g.items.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between p-3 rounded-xl bg-slate-50"
                  >
                    <div>
                      <div className="font-heebo text-slate-800">{s.company}</div>
                      {s.notes && (
                        <div className="text-xs text-slate-500">{s.notes}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="num-pop text-slate-800">{fmtShekel(s.amount)}</div>
                      <button
                        type="button"
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition"
                        onClick={() =>
                          setEditing({
                            id: s.id,
                            type: s.type,
                            company: s.company,
                            amount: s.amount,
                            notes: s.notes || "",
                          })
                        }
                        data-testid={`edit-saving-${s.id}`}
                        title="ערוך"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        type="button"
                        className="p-2 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition"
                        onClick={async () => {
                          if (confirm(`למחוק את "${s.company}"?`)) {
                            try {
                              await del(s.id);
                            } catch (err) {
                              alert("שגיאה במחיקה: " + (err?.message || err));
                            }
                          }
                        }}
                        data-testid={`delete-saving-${s.id}`}
                        title="מחק"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "עריכת חסכון" : "חסכון חדש"}
        testId="saving-modal"
        footer={
          <>
            <button className="btn-primary" onClick={save} data-testid="save-saving-btn">
              שמור
            </button>
            <button className="btn-secondary" onClick={() => setEditing(null)}>
              ביטול
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <div className="text-xs text-slate-500 mb-1 font-heebo">סוג חסכון</div>
            <select
              className="input-field"
              value={editing?.type || "pension"}
              onChange={(e) => setEditing((v) => ({ ...v, type: e.target.value }))}
              data-testid="saving-type-select"
            >
              {TYPES.map((t) => (
                <option key={t.k} value={t.k}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1 font-heebo">חברה / בנק</div>
            <input
              className="input-field"
              value={editing?.company || ""}
              onChange={(e) => setEditing((v) => ({ ...v, company: e.target.value }))}
              placeholder="לדוגמה: מנורה מבטחים, בנק הפועלים…"
              data-testid="saving-company-input"
            />
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1 font-heebo">סכום צבירה (₪)</div>
            <input
              type="number"
              step="0.01"
              className="input-field"
              value={editing?.amount ?? ""}
              onChange={(e) => setEditing((v) => ({ ...v, amount: e.target.value }))}
              data-testid="saving-amount-input"
            />
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1 font-heebo">הערות</div>
            <input
              className="input-field"
              value={editing?.notes || ""}
              onChange={(e) => setEditing((v) => ({ ...v, notes: e.target.value }))}
            />
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
